import { simpleHash } from '@balance-web/utils';

import { useAvatarStackContext } from './context';
import type { AvatarSizeType, CustomSizeType } from './types';

/**
 * Tokens are strings, exact sizes are objects — narrow on that rather than
 * probing for fields, so a future field on the object can't change the answer.
 */
export const isCustomAvatarSize = (
  size: AvatarSizeType
): size is CustomSizeType => {
  return typeof size !== 'string';
};

const TEXT_FALLBACK = '?';

export function getKeyFromText(keys: string[], text: string = TEXT_FALLBACK) {
  const hash = simpleHash(text);
  const key = keys[hash % keys.length];

  return key;
}

export function getInitials(text?: string, fallback = TEXT_FALLBACK) {
  if (!text || typeof text !== 'string') {
    return fallback;
  }
  if (text.length <= 2) {
    return text.toUpperCase();
  } // assume the consumer has provided custom initials

  return text
    .replace(/\s+/, ' ') // Repeated spaces results in empty strings
    .split(' ')
    .slice(0, 2)
    .map((v) => {
      return v && v[0].toUpperCase();
    }) // Watch out for empty strings
    .join('');
}

type HookOptions = {
  /** The max character length the initials are allowed to be. */
  max: 1 | 2;
  /** The name of the person represented by the avatar. */
  name: string;
  /** The size of the avatar. */
  size?: AvatarSizeType;
};

export const useInitials = ({ max, name, size }: HookOptions) => {
  const stackContext = useAvatarStackContext();
  const resolvedSize = stackContext?.size || size;
  // Only the 'xsmall' token is too narrow for two characters; an exact size is
  // one the caller has measured for themselves.
  const allowedCharLength = resolvedSize === 'xsmall' ? 1 : max;

  return getInitials(name).slice(0, allowedCharLength);
};
